import { addDays, endOfDay } from 'date-fns'
import Cookies from 'js-cookie'
import { Dispatch } from 'redux'

import { apiSdk } from '@app/api/endpoints'
import { Field } from '@app/api/types'
import { AppDispatch } from '@app/store/store'
import catchErrorHandler from '@app/store/utils/catchErrorHandler'
import { addNotification, NotificationActionType } from '@app/store/notifications'
import {
  CheckIfZipCodeIsSupportedMutation,
  CreateLeadInput,
  GetAvailableTimeSlotsQuery,
  GetCurrentTimeUtcQuery,
  ZipCodeIsSupportedInput
} from '@app/graphql/service/schema'
import { runtimeConfig } from '@app/common/config'
import { useCheckSetFieldValueResponse } from '@app/hooks/useCheckSetFieldValueResponse'
import { ERROR_MESSAGES, TIME_SLOTS_TABLE_DAYS_QUANTITY } from '@app/constants/booking'
import { BOOKING_FLOW_USER_ID_COOKIE, BOOKING_FLOW_USER_ID_COOKIE_EXPIRATION_DAYS, PROVIDERS } from '@app/constants'

import { actions } from './slice'
import { BookingInputFieldsNames, BookingSteps } from './types'
import { API_FIELDS_DATA_KEYS } from '@app/constants/fields'

const CHANNEL_ID = runtimeConfig.joinSproutTherapyConfig?.CHANNEL_ID
export const BOOKING_NOTIFICATION_AUTO_CLOSE_TIME = 30000

export const initBookingFlow = () => async (dispatch: Dispatch) => {
  const CHANNEL_ID = runtimeConfig.joinSproutTherapyConfig?.CHANNEL_ID
  const SERVICE_ID = runtimeConfig.joinSproutTherapyConfig?.SERVICE_ID

  dispatch(actions.requestInitBookingUserId())

  try {
    const response = await apiSdk.initSession({
      channelId: CHANNEL_ID,
      serviceId: SERVICE_ID,
      providers: PROVIDERS
    })
    if (!response.channel?.session?.init?.id) {
      console.error('Can not init booking flow user id')
    } else {
      new Promise(resolve => {
        return resolve(
          Cookies.set(BOOKING_FLOW_USER_ID_COOKIE, response.channel?.session?.init?.id, {
            expires: BOOKING_FLOW_USER_ID_COOKIE_EXPIRATION_DAYS
          })
        )
      }).then(() => dispatch(actions.successInitBookingUserId(response.channel?.session?.init?.id)))
    }
    dispatch(actions.setInitialCreateLeadStatus())
  } catch (error) {
    catchErrorHandler(error, dispatch, actions.errorInitBookingUserId)
  }
}

export const getAvailableTimeSlotsAction = (sessionId: string) => async (dispatch: Dispatch) => {
  try {
    const dateFrom = endOfDay(new Date())
    const dateTo = endOfDay(addDays(dateFrom, TIME_SLOTS_TABLE_DAYS_QUANTITY))

    const rawDates: GetAvailableTimeSlotsQuery = await apiSdk.GetAvailableTimeSlots({
      channelId: CHANNEL_ID,
      sessionId,
      fromDate: dateFrom.toISOString(),
      toDate: dateTo.toISOString()
    })

    const rawTimeSlots = rawDates.channels?.[0]?.sessions?.[0]?.timeSlots
    const timeSlots = rawTimeSlots ? rawTimeSlots.map(rawTimeSlot => new Date(rawTimeSlot?.datetime).getTime()) : []
    dispatch(
      actions.setTimeSlots({ dateFrom: new Date(dateFrom).getTime(), dateTo: new Date(dateTo).getTime(), timeSlots })
    )
  } catch (error) {
    catchErrorHandler(error, dispatch, actions.errorBackgroundRequest)
    dispatch(
      addNotification({
        autoCloseTime: BOOKING_NOTIFICATION_AUTO_CLOSE_TIME,
        message: ERROR_MESSAGES.backgroundRequest,
        actionType: NotificationActionType.Reload
      })
    )
  }
}

export const getCurrentUtcTime = () => async (dispatch: AppDispatch) => {
  try {
    const result: GetCurrentTimeUtcQuery = await apiSdk.GetCurrentTimeUtc()
    dispatch(actions.setCurrentUtcTime(result.current_time_utc))
  } catch (error) {
    console.error(error.message)
    const currentUtcTime = new Date().toISOString()
    dispatch(actions.setCurrentUtcTime(currentUtcTime))
  }
}

export const updateValue = (sessionId: string, field: Field) => async (dispatch: AppDispatch) => {
  dispatch(actions.requestUpdateBookingInputData())

  try {
    if (field.apiFieldKey) {
      const response = await apiSdk.SetFieldValue({
        channelId: CHANNEL_ID,
        sessionId,
        field: field.apiFieldKey,
        value: field.fieldValue
      })

      useCheckSetFieldValueResponse(response)
    }

    dispatch(actions.successUpdateBookingInputData(field))
  } catch (error) {
    catchErrorHandler(error, dispatch, actions.errorUpdateBookingInputData)
  }
}

// setting inputData.zipCode and isZipCodeSupported here (set them once to avoid inconsistency)
export const checkIfZipCodeIsSupported = (
  data: ZipCodeIsSupportedInput,
  sessionId: string,
  onSuccess?: (isZipCodeSupported: boolean) => void,
  onError?: () => void
) => async (dispatch: AppDispatch) => {
  dispatch(actions.requestUpdateBookingInputData())

  try {
    const response: CheckIfZipCodeIsSupportedMutation = await apiSdk.CheckIfZipCodeIsSupported({ data })
    const isZipCodeSupported = response.zipCode.isSupported.isSupported

    // update inputData at once
    dispatch(
      actions.successUpdateBookingInputData([
        {
          fieldName: BookingInputFieldsNames.zipCode,
          fieldValue: data.zipCode
        },
        {
          fieldName: BookingInputFieldsNames.isZipCodeSupported,
          fieldValue: `${isZipCodeSupported}`
        }
      ])
    )

    // update API_FIELDS_DATA_KEYS.zip
    // move here from ZipCodeScreen/index.tsx/handleGetStartedClick() to have it centralized
    // (moreover, some user flows do not call handleGetStartedClick())
    dispatch(
      updateValue(sessionId, {
        apiFieldKey: API_FIELDS_DATA_KEYS.zip,
        fieldName: BookingInputFieldsNames.zipCode,
        fieldValue: data.zipCode
      })
    )

    onSuccess?.(isZipCodeSupported)
  } catch (error) {
    catchErrorHandler(error, dispatch, actions.errorUpdateBookingInputData)
    onError?.()
  }
}

export const createLead = (inputData: CreateLeadInput) => async (dispatch: AppDispatch) => {
  dispatch(actions.requestCreateLead())
  try {
    const response = await apiSdk.CreateLead({ input: inputData })
    if (response.createLead.__typename === 'CreateLeadError') {
      throw new Error(response.createLead.code)
    }
    dispatch(actions.successCreateLead())
  } catch (error) {
    catchErrorHandler(error, dispatch, actions.errorCreateLead)
  }
}

export const confirmBooking = (sessionId: string) => async (dispatch: AppDispatch) => {
  dispatch(actions.requestConfirmBooking())
  try {
    await apiSdk.ConfirmBooking({
      channelId: CHANNEL_ID,
      sessionId
    })
    dispatch(actions.successConfirmBooking())
  } catch (error) {
    catchErrorHandler(error, dispatch, actions.errorConfirmBooking)
    throw new Error(error.message)
  }
}

export const setBookingStep = (step: BookingSteps, backStep: boolean = false) => async (dispatch: AppDispatch) => {
  try {
    dispatch(actions.setBookingStep({ step, backStep }))
  } catch (error) {
    console.error(error.message)
  }
}

export const setStepsNumber = (stepsNumber: number) => async (dispatch: AppDispatch) => {
  try {
    dispatch(actions.setStepsNumber(stepsNumber))
  } catch (error) {
    console.error(error.message)
  }
}
